import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Tabs, Table,
  Modal, Form, Input, Select, message, Alert, Spin,
  Empty, Result, Radio,
} from 'antd';
import {
  FacebookOutlined, RocketOutlined, ThunderboltOutlined,
  AimOutlined, MailOutlined, TeamOutlined, GlobalOutlined, AppstoreOutlined,
  CheckCircleOutlined, CloseCircleOutlined, BulbOutlined,
  SendOutlined, LinkOutlined, EditOutlined,
  CrownOutlined, UnorderedListOutlined, SafetyCertificateOutlined,
  LikeOutlined, CommentOutlined, PictureOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { Avatar, Tooltip, Statistic, Badge, Image } from 'antd';
import { facebookAPI, blogAPI, landingPageAPI, emailCampaignAPI, contactsAPI, scenarioAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function FacebookHubPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const [blogPosts, setBlogPosts] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  // Direct post
  const [directMsg, setDirectMsg] = useState('');
  const [directLink, setDirectLink] = useState('');
  const [directImg, setDirectImg] = useState('');
  const [directPosting, setDirectPosting] = useState(false);
  const [directResult, setDirectResult] = useState(null);

  // Devdoc: Post list
  const [fbPosts, setFbPosts] = useState([]);
  const [fbPostsLoading, setFbPostsLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDetailLoading, setPostDetailLoading] = useState(false);

  // Devdoc: Token status
  const [tokenStatus, setTokenStatus] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  const fetchFbPosts = async () => {
    setFbPostsLoading(true);
    try {
      const data = await facebookAPI.getPosts(20);
      setFbPosts(Array.isArray(data) ? data : []);
    } catch (e) { message.error('載入貼文失敗: ' + e.message); }
    finally { setFbPostsLoading(false); }
  };

  const checkPostDetail = async (postId) => {
    setPostDetailLoading(true);
    try {
      const data = await facebookAPI.getPostStatus(postId);
      setSelectedPost(data);
    } catch (e) { message.error('查詢失敗: ' + e.message); }
    finally { setPostDetailLoading(false); }
  };

  const checkTokenStatus = async () => {
    setTokenLoading(true);
    try {
      const data = await facebookAPI.getTokenStatus();
      setTokenStatus(data);
    } catch (e) { setTokenStatus({ ok: false, error: e.message }); }
    finally { setTokenLoading(false); }
  };

  // AI generate
  const [aiForm] = Form.useForm();
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Module create modal
  const [createModal, setCreateModal] = useState({ open: false, type: null, sourceId: null, sourceName: '' });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [postType, setPostType] = useState('page_post');

  // Blog post to FB
  const [blogModal, setBlogModal] = useState({ open: false, post: null });
  const [blogPosting, setBlogPosting] = useState(false);
  const [blogResult, setBlogResult] = useState(null);
  const [blogCustomMsg, setBlogCustomMsg] = useState('');

  // Cross-platform
  const [crossModal, setCrossModal] = useState(false);
  const [crossForm] = Form.useForm();
  const [crossCreating, setCrossCreating] = useState(false);
  const [crossResult, setCrossResult] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await facebookAPI.getStatus();
      setStatus(res.data || res);
    } catch (e) { console.error(e); }

    try {
      const [blogRes, lpRes, ecRes, clRes, scRes] = await Promise.all([
        blogAPI.getPosts({ page_size: 50, is_published: true }).catch(() => ({ data: { posts: [] } })),
        landingPageAPI.list().catch(() => ({ data: [] })),
        emailCampaignAPI.list().catch(() => ({ data: [] })),
        contactsAPI.listLists().catch(() => ({ data: [] })),
        scenarioAPI.list().catch(() => ({ data: [] })),
      ]);
      const bp = blogRes?.data?.posts || blogRes?.data || [];
      setBlogPosts(Array.isArray(bp) ? bp : []);
      setLandingPages(Array.isArray(lpRes.data) ? lpRes.data : (Array.isArray(lpRes) ? lpRes : []));
      setEmailCampaigns(Array.isArray(ecRes.data) ? ecRes.data : (Array.isArray(ecRes) ? ecRes : []));
      setContactLists(Array.isArray(clRes.data) ? clRes.data : (Array.isArray(clRes) ? clRes : []));
      setScenarios(Array.isArray(scRes.data) ? scRes.data : (Array.isArray(scRes) ? scRes : []));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Direct post ───────────────────────────────────────
  const handleDirectPost = async () => {
    if (!directMsg.trim()) { message.warning('請輸入貼文內容'); return; }
    setDirectPosting(true);
    setDirectResult(null);
    try {
      const payload = { message: directMsg.trim() };
      if (directLink.trim()) payload.link = directLink.trim();
      if (directImg.trim()) payload.image_url = directImg.trim();
      const raw = await facebookAPI.directPost(payload);
      const res = raw.data || raw;
      setDirectResult(res);
      if (res?.ok) {
        message.success('已發布到 Facebook!');
        setDirectMsg('');
        setDirectLink('');
        setDirectImg('');
      } else {
        message.error(res?.error || '發布失敗');
      }
    } catch (e) { message.error('請求失敗'); }
    setDirectPosting(false);
  };

  // ── AI generate ───────────────────────────────────────
  const handleAIGenerate = async (values) => {
    setAiGenerating(true);
    setAiResult(null);
    try {
      const raw = await facebookAPI.aiGenerateCopy(values);
      const res = raw.data || raw;
      if (res.ok) { setAiResult(res); message.success('AI 文案生成完成'); }
      else { message.error(res.error || '生成失敗'); }
    } catch (e) { message.error('請求失敗'); }
    setAiGenerating(false);
  };

  // ── Module create ─────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    setCreateResult(null);
    try {
      let res, raw;
      const { type, sourceId } = createModal;
      if (type === 'landing_page') {
        raw = await facebookAPI.createFromLandingPage({ landing_page_id: sourceId, post_type: postType });
      } else if (type === 'email_campaign') {
        raw = await facebookAPI.createFromEmailCampaign({ email_campaign_id: sourceId, post_type: postType });
      } else if (type === 'contact_list') {
        raw = await facebookAPI.createFromContactList({
          contact_list_id: sourceId, product_info: createModal.productInfo || 'SPO Platform', post_type: 'ad',
        });
      } else if (type === 'scenario') {
        raw = await facebookAPI.createFromScenario({ scenario_id: sourceId, post_type: postType });
      }
      res = raw?.data || raw;
      setCreateResult(res);
      if (res?.ok) message.success('Facebook 發布成功');
      else message.error(res?.error || '建立失敗');
    } catch (e) { message.error('請求失敗'); }
    setCreating(false);
  };

  // ── Blog post ─────────────────────────────────────────
  const handleBlogPost = async () => {
    setBlogPosting(true);
    setBlogResult(null);
    try {
      const payload = { blog_post_id: blogModal.post.id };
      if (blogCustomMsg.trim()) payload.custom_message = blogCustomMsg.trim();
      const raw = await facebookAPI.postBlog(payload);
      const res = raw.data || raw;
      setBlogResult(res);
      if (res?.ok) message.success('Blog 文章已發布到 Facebook');
      else message.error(res?.error || '發布失敗');
    } catch (e) { message.error('請求失敗'); }
    setBlogPosting(false);
  };

  // ── Cross-platform ────────────────────────────────────
  const handleCrossCreate = async (values) => {
    setCrossCreating(true);
    setCrossResult(null);
    try {
      const raw = await facebookAPI.crossPlatformCampaign(values);
      const res = raw.data || raw;
      setCrossResult(res);
      if (res?.ok) message.success('跨平台廣告建立完成');
      else message.error('部分平台建立失敗');
    } catch (e) { message.error('請求失敗'); }
    setCrossCreating(false);
  };

  const openCreate = (type, sourceId, sourceName) => {
    setCreateResult(null);
    setPostType('page_post');
    setCreateModal({ open: true, type, sourceId, sourceName, productInfo: '' });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#999' }}>Loading...</div>
      </div>
    );
  }

  const isConfigured = status?.configured;

  const tabItems = [
    // ── Tab 1: Direct Post (default) ──
    {
      key: 'post',
      label: <span><EditOutlined /> 直接發文</span>,
      children: (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          {!isConfigured && (
            <Alert type="warning" showIcon message="Facebook 尚未設定" style={{ marginBottom: 16, borderRadius: 8 }} />
          )}
          {directResult?.ok ? (
            <Result status="success" title="已發布到 Facebook!"
              extra={[
                directResult.post_url && <Button key="view" type="primary" href={directResult.post_url} target="_blank" icon={<FacebookOutlined />} style={{ background: '#1877F2', border: 'none' }}>查看貼文</Button>,
                <Button key="new" onClick={() => setDirectResult(null)} icon={<EditOutlined />}>再發一篇</Button>,
              ]}
            />
          ) : (
            <Card style={{ borderRadius: 12 }}>
              <Form layout="vertical">
                <Form.Item label="貼文內容" required
                  help="輸入你要發布的內容，可搭配下方 AI 生成功能">
                  <TextArea
                    rows={6}
                    value={directMsg}
                    onChange={e => setDirectMsg(e.target.value)}
                    placeholder="在這裡輸入你的 Facebook 貼文內容...&#10;&#10;支援 emoji、換行、hashtag 等"
                    style={{ fontSize: 16 }}
                    maxLength={5000}
                    showCount
                  />
                </Form.Item>
                <Form.Item label={<span><LinkOutlined /> 附加連結 <Text type="secondary">(選填)</Text></span>}>
                  <Input
                    value={directLink}
                    onChange={e => setDirectLink(e.target.value)}
                    placeholder="https://..."
                    size="large"
                  />
                </Form.Item>
                <Form.Item label={<span>圖片網址 <Text type="secondary">(選填)</Text></span>}>
                  <Input
                    value={directImg}
                    onChange={e => setDirectImg(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    size="large"
                  />
                </Form.Item>
                {directResult && !directResult.ok && (
                  <Alert type="error" showIcon message={directResult.error} style={{ marginBottom: 16, borderRadius: 8 }} />
                )}
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={directPosting}
                  onClick={handleDirectPost}
                  disabled={!isConfigured || !directMsg.trim()}
                  size="large"
                  block
                  style={{
                    height: 56, fontSize: 18,
                    background: isConfigured && directMsg.trim() ? '#1877F2' : undefined,
                    border: 'none',
                  }}
                >
                  發布到 Facebook
                </Button>
              </Form>
            </Card>
          )}
        </div>
      ),
    },

    // ── Tab 2: Blog ──
    {
      key: 'blog',
      label: <span><LinkOutlined /> Blog 發文</span>,
      children: (
        <>
          <Alert type="info" showIcon icon={<SendOutlined />}
            message="選擇已發布的 Blog 文章，一鍵發布到 Facebook 粉絲專頁"
            style={{ marginBottom: 16, borderRadius: 12 }} />
          <Table dataSource={blogPosts} rowKey="id" pagination={{ pageSize: 8 }}
            scroll={{ x: 500 }}
            columns={[
              { title: '標題', dataIndex: 'title', ellipsis: true },
              { title: '時間', dataIndex: 'published_at', width: 100, render: v => v ? new Date(v).toLocaleDateString('zh-TW') : '-' },
              { title: '', width: 120, render: (_, r) => (
                <Button type="primary" size="small" icon={<FacebookOutlined />} disabled={!isConfigured}
                  onClick={() => { setBlogResult(null); setBlogCustomMsg(''); setBlogModal({ open: true, post: r }); }}
                  style={{ background: '#1877F2', border: 'none' }}>
                  發布
                </Button>
              )},
            ]} locale={{ emptyText: <Empty description="尚無 Blog 文章" /> }} />
        </>
      ),
    },

    // ── Tab 3: AI Copy ──
    {
      key: 'ai',
      label: <span><BulbOutlined /> AI 文案</span>,
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="輸入產品資訊" size="small" style={{ borderRadius: 12 }} extra={<Tag color="blue">Claude AI</Tag>}>
              <Form form={aiForm} layout="vertical" onFinish={handleAIGenerate}>
                <Form.Item name="product_info" label="產品 / 服務描述" rules={[{ required: true, message: '請輸入' }]}>
                  <TextArea rows={4} placeholder="描述你的產品或服務..." />
                </Form.Item>
                <Form.Item name="target_audience" label="目標受眾（選填）">
                  <Input placeholder="例如：25-45 歲女性、電商經營者" />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="post_type" label="類型" initialValue="page_post">
                      <Select>
                        <Select.Option value="page_post">粉專貼文</Select.Option>
                        <Select.Option value="ad">廣告文案</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="language" label="語言" initialValue="zh-TW">
                      <Select>
                        <Select.Option value="zh-TW">繁體中文</Select.Option>
                        <Select.Option value="en">English</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Button type="primary" htmlType="submit" icon={<ThunderboltOutlined />} loading={aiGenerating} block
                  style={{ background: '#1877F2', border: 'none', height: 44 }}>
                  AI 生成文案
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="生成結果" size="small" style={{ borderRadius: 12, minHeight: 300 }}>
              {aiGenerating ? (
                <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /><div style={{ marginTop: 12 }}>AI 生成中...</div></div>
              ) : aiResult?.copy ? (
                <div>
                  {aiResult.copy.post_text && (
                    <>
                      <Text strong>貼文內容</Text>
                      <Paragraph
                        copyable
                        style={{ background: '#f0f5ff', padding: 12, borderRadius: 8, marginTop: 8, whiteSpace: 'pre-wrap' }}
                      >
                        {aiResult.copy.post_text}
                      </Paragraph>
                      <Button size="small" type="link" onClick={() => {
                        setDirectMsg(aiResult.copy.post_text + (aiResult.copy.hashtags ? '\n\n' + aiResult.copy.hashtags.map(h => '#' + h.replace('#', '')).join(' ') : ''));
                        message.success('已複製到直接發文');
                      }}>
                        <SendOutlined /> 用這個內容直接發文
                      </Button>
                    </>
                  )}
                  {aiResult.copy.primary_text && (
                    <div style={{ marginTop: 12 }}>
                      <Text strong>廣告主文字</Text>
                      <Paragraph copyable style={{ background: '#f0f5ff', padding: 8, borderRadius: 8, marginTop: 4 }}>{aiResult.copy.primary_text}</Paragraph>
                    </div>
                  )}
                  {aiResult.copy.headline && <div style={{ marginTop: 8 }}><Text strong>標題：</Text> <Tag color="blue">{aiResult.copy.headline}</Tag></div>}
                  {aiResult.copy.hashtags && (
                    <div style={{ marginTop: 8 }}>
                      {aiResult.copy.hashtags.map((h, i) => <Tag key={i} color="geekblue" style={{ marginBottom: 4 }}>#{h.replace('#','')}</Tag>)}
                    </div>
                  )}
                </div>
              ) : (
                <Empty description="輸入產品資訊後點擊生成" />
              )}
            </Card>
          </Col>
        </Row>
      ),
    },

    // ── Tab 4: Landing Pages ──
    {
      key: 'lp',
      label: <span><GlobalOutlined /> LP</span>,
      children: (
        <>
          <Alert type="info" showIcon icon={<RocketOutlined />}
            message="Landing Page → Facebook" description="一鍵將 Landing Page 發布到粉專或建立流量廣告"
            style={{ marginBottom: 16, borderRadius: 12 }} />
          <Table dataSource={landingPages} rowKey="id" pagination={{ pageSize: 8 }}
            scroll={{ x: 400 }}
            columns={[
              { title: '標題', dataIndex: 'title', ellipsis: true },
              { title: '狀態', dataIndex: 'is_published', width: 70, render: v => v ? <Tag color="green">發布</Tag> : <Tag>草稿</Tag> },
              { title: '', width: 100, render: (_, r) => (
                <Button type="primary" size="small" icon={<SendOutlined />} disabled={!r.is_published || !isConfigured}
                  onClick={() => openCreate('landing_page', r.id, r.title)} style={{ background: '#1877F2', border: 'none' }}>
                  發布
                </Button>
              )},
            ]} locale={{ emptyText: <Empty description="尚無 Landing Page" /> }} />
        </>
      ),
    },

    // ── Tab 5: Email ──
    {
      key: 'email',
      label: <span><MailOutlined /> Email</span>,
      children: (
        <>
          <Alert type="info" showIcon icon={<AimOutlined />}
            message="Email → Facebook 再行銷"
            style={{ marginBottom: 16, borderRadius: 12 }} />
          <Table dataSource={emailCampaigns} rowKey="id" pagination={{ pageSize: 8 }}
            scroll={{ x: 400 }}
            columns={[
              { title: '名稱', dataIndex: 'name', ellipsis: true },
              { title: '主旨', dataIndex: 'subject', ellipsis: true },
              { title: '', width: 100, render: (_, r) => (
                <Button type="primary" size="small" icon={<SendOutlined />} disabled={!isConfigured}
                  onClick={() => openCreate('email_campaign', r.id, r.name)} style={{ background: '#1877F2', border: 'none' }}>
                  發布
                </Button>
              )},
            ]} locale={{ emptyText: <Empty description="尚無 Email 活動" /> }} />
        </>
      ),
    },

    // ── Tab 6: Contacts ──
    {
      key: 'contacts',
      label: <span><TeamOutlined /> CRM</span>,
      children: (
        <>
          <Alert type="info" showIcon icon={<TeamOutlined />}
            message="CRM → Facebook Ads"
            style={{ marginBottom: 16, borderRadius: 12 }} />
          <Table dataSource={contactLists} rowKey="id" pagination={{ pageSize: 8 }}
            scroll={{ x: 400 }}
            columns={[
              { title: '名單', dataIndex: 'name', ellipsis: true },
              { title: '人數', dataIndex: 'member_count', width: 70 },
              { title: '', width: 100, render: (_, r) => (
                <Button type="primary" size="small" icon={<SendOutlined />} disabled={!isConfigured}
                  onClick={() => openCreate('contact_list', r.id, r.name)} style={{ background: '#1877F2', border: 'none' }}>
                  廣告
                </Button>
              )},
            ]} locale={{ emptyText: <Empty description="尚無聯絡人名單" /> }} />
        </>
      ),
    },

    // ── Tab 7: Scenarios ──
    {
      key: 'scenarios',
      label: <span><AppstoreOutlined /> 情境</span>,
      children: (
        <>
          <Alert type="info" showIcon icon={<AppstoreOutlined />}
            message="行銷情境 → Facebook"
            style={{ marginBottom: 16, borderRadius: 12 }} />
          <Table dataSource={scenarios} rowKey="id" pagination={{ pageSize: 8 }}
            scroll={{ x: 400 }}
            columns={[
              { title: '情境名稱', dataIndex: 'name', ellipsis: true },
              { title: '狀態', dataIndex: 'status', width: 80, render: v => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
              { title: '', width: 100, render: (_, r) => (
                <Button type="primary" size="small" icon={<SendOutlined />} disabled={!isConfigured}
                  onClick={() => openCreate('scenario', r.id, r.name)} style={{ background: '#1877F2', border: 'none' }}>
                  發布
                </Button>
              )},
            ]} locale={{ emptyText: <Empty description="尚無行銷情境" /> }} />
        </>
      ),
    },

    // ── Tab 8: 最新貼文列表（devdoc: 查詢最新貼文列表）──
    {
      key: 'fb-posts',
      label: <span><UnorderedListOutlined /> 貼文記錄</span>,
      children: (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Text type="secondary">從 Facebook 粉專讀取最新 20 篇貼文</Text>
            <Button icon={<ReloadOutlined />} onClick={fetchFbPosts} loading={fbPostsLoading} size="small">
              載入 / 更新
            </Button>
          </div>
          {fbPosts.length === 0 && !fbPostsLoading && (
            <Alert type="info" showIcon message="點擊「載入 / 更新」從 Facebook Graph API 取得貼文列表" />
          )}
          <Table
            dataSource={fbPosts}
            rowKey="id"
            loading={fbPostsLoading}
            pagination={{ pageSize: 10 }}
            columns={[
              {
                title: '貼文內容', dataIndex: 'message', ellipsis: true,
                render: (msg, r) => (
                  <div>
                    <Text style={{ fontSize: 13 }}>{(msg || '（無文字）').slice(0, 120)}</Text>
                    {r.full_picture && <div><img src={r.full_picture} alt="" style={{ width: 60, height: 40, objectFit: 'cover', marginTop: 4, borderRadius: 4 }} /></div>}
                  </div>
                ),
              },
              {
                title: '狀態', width: 90,
                render: (_, r) => <Tag color={r.is_published ? 'green' : 'orange'}>{r.is_published ? '已發布' : '草稿'}</Tag>,
              },
              {
                title: '時間', dataIndex: 'created_time', width: 140,
                render: v => v ? new Date(v).toLocaleString('zh-TW') : '-',
              },
              {
                title: '操作', width: 120,
                render: (_, r) => (
                  <Space>
                    {r.permalink_url && (
                      <Button size="small" icon={<FacebookOutlined />} href={r.permalink_url} target="_blank" style={{ color: '#1877F2' }}>
                        查看
                      </Button>
                    )}
                    <Button size="small" loading={postDetailLoading} onClick={() => checkPostDetail(r.id)}>
                      詳情
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
          {selectedPost && (
            <Card title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> 貼文詳細狀態</Space>}
              style={{ marginTop: 16 }} extra={<Button size="small" onClick={() => setSelectedPost(null)}>關閉</Button>}>
              <Row gutter={16}>
                <Col span={6}><Statistic title="讚" value={selectedPost.likes?.summary?.total_count || 0} prefix={<LikeOutlined />} /></Col>
                <Col span={6}><Statistic title="留言" value={selectedPost.comments?.summary?.total_count || 0} prefix={<CommentOutlined />} /></Col>
                <Col span={6}><Tag color={selectedPost.is_published ? 'green' : 'red'}>{selectedPost.is_published ? '已發布' : '草稿'}</Tag></Col>
                <Col span={6}><Tag>{selectedPost.privacy?.value || '-'}</Tag></Col>
              </Row>
              {selectedPost.full_picture && <img src={selectedPost.full_picture} alt="" style={{ marginTop: 12, maxWidth: 300, borderRadius: 8 }} />}
              {selectedPost.permalink_url && (
                <div style={{ marginTop: 12 }}>
                  <a href={selectedPost.permalink_url} target="_blank" rel="noreferrer">{selectedPost.permalink_url}</a>
                </div>
              )}
            </Card>
          )}
        </div>
      ),
    },

    // ── Tab 9: Token 狀態（devdoc: 驗證 Token 狀態）──
    {
      key: 'token-status',
      label: <span><SafetyCertificateOutlined /> Token 狀態</span>,
      children: (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Alert type="info" showIcon
            message="驗證 Facebook Page Access Token 是否有效"
            description="透過 Graph API debug_token 端點確認 token 狀態、到期時間及授權範圍。"
            style={{ marginBottom: 24 }}
          />
          <Button type="primary" icon={<SafetyCertificateOutlined />} loading={tokenLoading}
            onClick={checkTokenStatus} size="large" style={{ marginBottom: 24 }}>
            驗證 Token 狀態
          </Button>
          {tokenStatus && (
            <Card style={{ borderRadius: 12 }}>
              <Row gutter={16}>
                <Col span={24} style={{ marginBottom: 16 }}>
                  <Tag color={tokenStatus.ok && tokenStatus.valid ? 'green' : 'red'} style={{ fontSize: 14, padding: '4px 12px' }}>
                    {tokenStatus.ok && tokenStatus.valid ? '✅ Token 有效' : '❌ Token 無效'}
                  </Tag>
                </Col>
                {tokenStatus.ok && (
                  <>
                    <Col span={12}><Statistic title="粉專 ID" value={tokenStatus.page_id || '-'} /></Col>
                    <Col span={12}><Statistic title="帳號名稱" value={tokenStatus.name || '-'} /></Col>
                    {tokenStatus.debug?.scopes?.length > 0 && (
                      <Col span={24} style={{ marginTop: 12 }}>
                        <Text type="secondary">授權範圍：</Text>
                        <div style={{ marginTop: 4 }}>
                          {tokenStatus.debug.scopes.map(s => <Tag key={s} color="blue">{s}</Tag>)}
                        </div>
                      </Col>
                    )}
                    {tokenStatus.debug?.expires_at && (
                      <Col span={24} style={{ marginTop: 12 }}>
                        <Text type="secondary">到期時間：</Text>
                        <Text style={{ marginLeft: 8 }}>
                          {tokenStatus.debug.expires_at === 0 ? '永不過期（長效 Token）' : new Date(tokenStatus.debug.expires_at * 1000).toLocaleString('zh-TW')}
                        </Text>
                      </Col>
                    )}
                  </>
                )}
                {tokenStatus.error && <Col span={24}><Alert type="error" message={tokenStatus.error} /></Col>}
              </Row>
            </Card>
          )}
          <Alert type="warning" showIcon style={{ marginTop: 24 }}
            message="Token 更新方式"
            description={
              <ol style={{ paddingLeft: 20, margin: 0 }}>
                <li>登入 <a href="https://developers.facebook.com/apps/2056447448517764" target="_blank" rel="noreferrer">Facebook 開發者後台</a></li>
                <li>Tools → Graph API Explorer → 選擇 App = BlogPostAI</li>
                <li>選擇粉專 = 宇聯科技 ULINK，勾選 pages_manage_posts</li>
                <li>至「Facebook 設定」頁面更新 Access Token</li>
              </ol>
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Card
        style={{ marginBottom: 16, background: 'linear-gradient(135deg, #1877F2 0%, #003d82 100%)', border: 'none', borderRadius: 12 }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <Row justify="space-between" align="middle" wrap>
          <Col>
            <Space size={12}>
              <FacebookOutlined style={{ fontSize: 36, color: '#fff' }} />
              <div>
                <Title level={3} style={{ color: '#fff', margin: 0, fontSize: 20 }}>Facebook 整合中心</Title>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                  發文 / 廣告 / AI 文案 / 跨平台投放
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space size={8}>
              <Tag
                color={isConfigured ? 'green' : 'red'}
                icon={isConfigured ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                style={{ fontSize: 13, padding: '2px 12px' }}
              >
                {isConfigured ? '已連線' : '未設定'}
              </Tag>
              <Button size="small" icon={<CrownOutlined />}
                style={{ borderColor: '#fff', color: '#fff', background: 'transparent' }}
                onClick={() => { setCrossResult(null); setCrossModal(true); }}>
                跨平台
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Tabs */}
      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: '12px 16px' }}>
        <Tabs
          defaultActiveKey="post"
          size="small"
          tabBarStyle={{ marginBottom: 12 }}
          items={tabItems}
        />
      </Card>

      {/* Blog → Facebook Modal */}
      <Modal
        title={<Space><FacebookOutlined style={{ color: '#1877F2' }} /> 發布 Blog 到 Facebook</Space>}
        open={blogModal.open}
        onCancel={() => { setBlogModal({ open: false, post: null }); setBlogResult(null); }}
        footer={blogResult ? [
          <Button key="close" onClick={() => { setBlogModal({ open: false, post: null }); setBlogResult(null); }}>關閉</Button>,
        ] : [
          <Button key="cancel" onClick={() => setBlogModal({ open: false, post: null })}>取消</Button>,
          <Button key="post" type="primary" icon={<SendOutlined />} loading={blogPosting} onClick={handleBlogPost}
            style={{ background: '#1877F2', border: 'none' }}>
            發布
          </Button>,
        ]}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        {blogResult ? (
          blogResult.ok ? (
            <Result status="success" title="已發布!"
              extra={blogResult.post_url && <Button type="link" href={blogResult.post_url} target="_blank" icon={<LinkOutlined />}>查看貼文</Button>}
            />
          ) : <Result status="error" title="發布失敗" subTitle={blogResult.error} />
        ) : blogModal.post ? (
          <div>
            <Card size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#f0f5ff' }}>
              <Text strong>{blogModal.post.title}</Text>
              {blogModal.post.excerpt && <Paragraph style={{ marginTop: 4, marginBottom: 0, color: '#666', fontSize: 13 }}>{blogModal.post.excerpt}</Paragraph>}
              {blogModal.post.tags && blogModal.post.tags.length > 0 && (
                <div style={{ marginTop: 8 }}>{blogModal.post.tags.map((t, i) => <Tag key={i} color="blue" style={{ fontSize: 11 }}>#{t}</Tag>)}</div>
              )}
            </Card>
            <Form.Item label="自訂訊息（選填）" style={{ marginBottom: 8 }}>
              <TextArea rows={3} value={blogCustomMsg} onChange={e => setBlogCustomMsg(e.target.value)}
                placeholder="留空 = 自動使用標題+摘要+Hashtags" />
            </Form.Item>
          </div>
        ) : null}
      </Modal>

      {/* Module Create Modal */}
      <Modal
        title={<Space><FacebookOutlined style={{ color: '#1877F2' }} /> 發布到 Facebook</Space>}
        open={createModal.open}
        onCancel={() => { setCreateModal({ ...createModal, open: false }); setCreateResult(null); }}
        footer={createResult ? [
          <Button key="close" onClick={() => { setCreateModal({ ...createModal, open: false }); setCreateResult(null); }}>關閉</Button>,
        ] : [
          <Button key="cancel" onClick={() => setCreateModal({ ...createModal, open: false })}>取消</Button>,
          <Button key="create" type="primary" icon={<RocketOutlined />} loading={creating} onClick={handleCreate}
            style={{ background: '#1877F2', border: 'none' }}>
            AI 生成 & 發布
          </Button>,
        ]}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        {createResult ? (
          createResult.ok ? (
            <Result status="success" title="發布成功"
              subTitle={createResult.post_url ? '貼文已發布到粉專' : '廣告已建立（暫停）'}
              extra={
                <Space direction="vertical">
                  {createResult.post_url && <Button type="link" href={createResult.post_url} target="_blank">查看貼文</Button>}
                  {createResult.ads_manager_url && <Button type="link" href={createResult.ads_manager_url} target="_blank">Ads Manager</Button>}
                </Space>
              }
            />
          ) : <Result status="error" title="發布失敗" subTitle={createResult.error} />
        ) : (
          <div>
            <Alert type="info" showIcon message={`來源：${createModal.sourceName}`} style={{ marginBottom: 12, borderRadius: 8 }} />
            {createModal.type !== 'contact_list' && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>發布方式</Text>
                <div style={{ marginTop: 4 }}>
                  <Radio.Group value={postType} onChange={e => setPostType(e.target.value)}>
                    <Radio.Button value="page_post">粉專貼文</Radio.Button>
                    <Radio.Button value="ad">建立廣告</Radio.Button>
                  </Radio.Group>
                </div>
              </div>
            )}
            {createModal.type === 'contact_list' && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>產品描述</Text>
                <TextArea rows={3} placeholder="AI 會結合受眾資訊生成文案" style={{ marginTop: 4 }}
                  value={createModal.productInfo} onChange={e => setCreateModal({ ...createModal, productInfo: e.target.value })} />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Cross-Platform Modal */}
      <Modal
        title={<Space><CrownOutlined style={{ color: '#faad14' }} /> 跨平台 FB + Google Ads</Space>}
        open={crossModal}
        onCancel={() => { setCrossModal(false); setCrossResult(null); }}
        footer={crossResult ? [
          <Button key="close" onClick={() => { setCrossModal(false); setCrossResult(null); }}>關閉</Button>,
        ] : null}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        {crossResult ? (
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                <FacebookOutlined style={{ color: '#1877F2', fontSize: 24 }} />
                <div style={{ marginTop: 4 }}>
                  {crossResult.platforms?.facebook?.ok
                    ? <Tag color="green">成功</Tag>
                    : <Tag color="red">失敗</Tag>}
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                <span style={{ fontSize: 24, color: '#4285F4' }}>G</span>
                <div style={{ marginTop: 4 }}>
                  {crossResult.platforms?.google_ads?.ok
                    ? <Tag color="green">成功</Tag>
                    : <Tag color="red">失敗</Tag>}
                </div>
              </Card>
            </Col>
          </Row>
        ) : (
          <Form form={crossForm} layout="vertical" onFinish={handleCrossCreate}>
            <Form.Item name="product_info" label="產品描述" rules={[{ required: true, message: '請輸入' }]}>
              <TextArea rows={4} placeholder="描述你的產品或服務..." />
            </Form.Item>
            <Form.Item name="target_audience" label="目標受眾（選填）">
              <Input placeholder="例如：中小企業 IT 主管" />
            </Form.Item>
            <Button type="primary" htmlType="submit" icon={<CrownOutlined />} loading={crossCreating} block
              style={{ background: 'linear-gradient(135deg, #1877F2, #4285F4)', border: 'none', height: 48 }}>
              同時投放 FB + Google Ads
            </Button>
          </Form>
        )}
      </Modal>
    </div>
  );
}
